import { GetServerSideProps } from 'next';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';

import Loader from '@components/Loader';
import DiscountNotification from '@components/forms/Recommendation/DiscountNotification';
import Wizard, {
  WizardPageProps,
} from '@components/forms/Recommendation/wizard';
import { pushToDataLayer } from '@lib/utils.gtm';
import { getDefaultPhonePrefix } from '@lib/utils';
import { createStorageKey } from '@lib/utils.persist';
import { useIsLoggedIn, useTranslation } from '@lib/hooks';
import { withRecommendationTokenValidator } from '@lib/hoc';
import {
  useAlvarref,
  useRecommendationInitialValues,
} from '@lib/hooks.recommendation';
import {
  getRecommendationState,
  saveOnRecommendationState,
} from '@lib/utils.recommendation';
import {
  validateBasicInfo,
  validateBodyCondition,
  validateDietShare,
  validateRecoFetch,
} from '@lib/utils.recommendation.validation';

const BasicDogInfoPage = dynamic(
  () => import('@components/forms/Recommendation/Dog'),
  { loading: () => <Loader /> },
);

const ActivityPage = dynamic(
  () => import('@components/forms/Recommendation/Activity'),
  { loading: () => <BasicDogInfoPage /> },
);
const DietCompositionPage = dynamic(
  () => import('@components/forms/Recommendation/DietComposition'),
  { loading: () => <ActivityPage /> },
);
const IngredientsAndNeeds = dynamic(
  () => import('@components/forms/Recommendation/IngredientsAndNeeds/index'),
  { loading: () => <DietCompositionPage /> },
);
const EmailPage = dynamic(
  () => import('@components/forms/Recommendation/Email'),
  { loading: () => <IngredientsAndNeeds /> },
);
const RecommendationPage = dynamic(
  () => import('@components/forms/Recommendation/Product'),
  { loading: () => <Loader /> },
);

const storageKey = createStorageKey('recommendation');

const Page = ({ children }: WizardPageProps) => (
  <>
    <DiscountNotification />
    {children}
  </>
);

const Recommendation = () => {
  const t = useTranslation();
  const { push } = useRouter();
  const { isLoggedIn, token: authToken } = useIsLoggedIn();
  const recommendationState = getRecommendationState();
  const initialValues = useRecommendationInitialValues(recommendationState);
  const { alvarref } = useAlvarref();

  if (authToken && !recommendationState.token) {
    saveOnRecommendationState({ token: authToken });
  }

  return (
    <Wizard
      id="dog"
      storageKey={storageKey}
      initialValues={initialValues}
      onSubmit={async () => {
        push('/recommendation/checkout');
      }}
      afterSubmitUrl={'/recommendation/checkout'}
    >
      <Page
        title={t('recommendation.pages.dog_info')}
        onSubmit={(values) => {
          pushToDataLayer({ shippingCountry: values.shipping_country });
          saveOnRecommendationState({
            countryCode: getDefaultPhonePrefix(values.shipping_country),
          });
        }}
        validate={(values) => validateBasicInfo(values, t)}
      >
        <BasicDogInfoPage />
      </Page>
      <Page
        title={t('recommendation.pages.activity')}
        validate={(values) => validateBodyCondition(values, t)}
      >
        <ActivityPage />
      </Page>
      <Page
        title={t('recommendation.pages.diet_composition')}
        validate={(values) => validateDietShare(values, t)}
      >
        <DietCompositionPage />
      </Page>
      <Page title={t('recommendation.pages.specialNeeds')}>
        <IngredientsAndNeeds />
      </Page>
      <Page
        title={t('email')}
        next={t('recommendation.show_recommendation')}
        blockNav={isLoggedIn}
        hideNav
        validate={(values) => validateRecoFetch(values, t)}
        onSubmit={() => {
          pushToDataLayer({ event: 'lead' });
        }}
        component={EmailPage}
      />
      <Page
        title={t('recommendation.pages.recommendation')}
        submit={t('recommendation.form.demo_order')}
        skipNav
        disableForwardNavClick
        next={t('recommendation.order_overview')}
        component={RecommendationPage}
      />
    </Wizard>
  );
};

export const getServerSideProps: GetServerSideProps = async () => ({
  props: {
    title: 'recommendation_machine',
    metadata: {
      description: 'meta_description.recommendation',
    },
  },
});

/* TODO: Add withRecommendationTokenValidator, which wraps all recommendation routes and takes the temporary token from sessionStorage. Then add ping, which checks if the 1h token is still valid.
 * If the token expires, clean storage and throw a modal telling the session expired and ask for email. Call initialize-user and renew the token for whatever uses this is now. If the user has dog saved,
 * load the dog. Only the wizard state will be lost
 */

export default withRecommendationTokenValidator(Recommendation);
